import {matchPath, useLocation} from "react-router-dom";

const devReplacementUrls = [
    "http://brio-saas.test",
    "http://schools.brio-saas.test",
    "http://teachers.brio-saas.test",
];

// Helper function to replace URLs based on environment
const replaceEnvironmentUrls = (url: string, replacement: string): string => {
    return devReplacementUrls.reduce((updatedUrl, devUrl) => {
        return updatedUrl.replace(devUrl, replacement);
    }, url);
};

export const replaceDevUrl = (url: string, replaceTenancy: boolean = true): string => {
    let updatedUrl = url;
    if (process.env.NODE_ENV === "development") {
        updatedUrl = replaceEnvironmentUrls(updatedUrl, process.env.REACT_APP_API_DOMAIN || "");
        if (replaceTenancy) {
            updatedUrl = updatedUrl.replace("/tenancy/assets", "/storage");
        }
    }
    return updatedUrl;
};


export const nicePosition = (position: string) => {
    switch (position) {
        case "principal":
            return "Director";
        case "teacher":
            return "Profesor";
        case "student":
            return "Elev";
        default:
            return position;
    }
};

export const niceUserName = (firstname: string, lastname: string, email?: string) => {
    return firstname === "" && lastname === ""
        ? email || "Nedefinit"
        : firstname + (firstname !== "" ? " " : "") + lastname;
};

export const niceUserNameWithSubjects = (firstname: string, lastname: string, subjects: any[], email?: string) => {
    return firstname === "" && lastname === ""
        ? email || "Nedefinit"
        : firstname + (firstname !== "" ? " " : "") + lastname + " (" + subjects.map((s: any) => s.name).join(", ") + ")";
};

export const gradesRomans = ["CP", "I", "II", "III", "IV", "V", "VI", "VII", "VIII", "IX", "X", "XI", "XII"];

export const getRomanNumber = (number: number) => {
    return gradesRomans[number];
};

type TError = {
    code: string;
    title?: string;
    message?: string;
    icon?: string;
    button?: string;
    closable?: boolean;
    returnFunction?: () => void;
    onClickFunction?: () => void;
};

export const errors = [
    {
        code: "A001",
        title: "Eroare comunicare cu server",
        message:
            "<p>Din păcate a intervenit o eroare la comunicarea cu serverul. Vă rugăm încercați mai târziu.</p><p>Dacă problema persistă, ne puteți contacta la <a href='mailto:contact@brio.ro'>contact@brio.ro</a>.</p>",
        icon: "fat face-eyes-xmarks",
        closable: true,
        button: "REÎNCARCĂ",
        returnFunction: () => {
            if (process.env.NODE_ENV !== "development") {
                window.location.reload();
            }
        },
    },
    {
        code: "A002",
        title: "Eroare comunicare cu server",
        message:
            "<p>Din păcate, serverul a trimis un mesaj de eroare. Vă rugăm încercați mai târziu.</p><p>Dacă problema persistă, ne puteți contacta la <a href='mailto:contact@brio.ro'>contact@brio.ro</a>.</p>",
        icon: "fat face-eyes-xmarks",
        closable: true,
        button: "REÎNCARCĂ",
        returnFunction: () => {
            if (process.env.NODE_ENV !== "development") {
                window.location.reload();
            }
        },
    },
    {
        code: "A003",
        title: "Verifică adresa de e-mail",
        message:
            "<p>Pentru a continua trebuie sa îți verifici adresa de e-mail</p>",
        icon: "fat envelope",
        closable: true,
        button: "ÎNCHIDE",
    },
    {
        code: "A004",
        title: "Adresa de e-mail a fost verificată cu succes",
        message:
            "<p></p>",
        icon: "fat envelope",
        closable: true,
        button: "ÎNCHIDE",
    },
    {
        code: "A005",
        title: "ATENTIE!",
        message:
            "<p>Sesiunea de testare este monitorizată la nivelul browserului.<p>" +
            "<p><b>Orice părăsire a ecranului în timpul testului (ALT+ TAB) va fi înregistrată și transmisă profesorului tău.</b></p>",
        icon: "fat circle-exclamation",
        closable: false,
        button: "Am înteles",
    },
    {
        code: "A006",
        title: "Sesiunea a expirat",
        message:
            "<p>Sesiunea dumneavoastră a expirat. Vă rugăm să vă conectați din nou.<p>",
        icon: "fat face-eyes-xmarks",
        closable: true,
        button: "Am înteles",
    },
];

export const errorMessage = (code: string): TError | null => {
    return errors.find((e) => e.code === code) || null;
};

export const arrayEquals = (a: any[], b: any[]) => {
    return (
        Array.isArray(a) &&
        Array.isArray(b) &&
        a.length === b.length &&
        a.every((val, index) => val === b[index])
    );
}

export const capitalize = (input: string): string => {
    return typeof input === "string" && input.length > 0
        ? input.charAt(0).toUpperCase() + input.slice(1)
        : "";
};

export function useRouteMatch(patterns: readonly string[]) {
    const {pathname} = useLocation();

    return patterns.find((pattern) => matchPath(pattern, pathname)) || null;
}

export const generateHeatMapData = (array: any[], type: "decile" | "risc" | "studentScore" = "decile"): any[] => {
    const graph: any[] = [];

    if (type === "studentScore") {
        const allSubcategories = Array.from(
            new Set(
                array.flatMap((student) =>
                    (student.subcategories || []).map((sub: any) => sub.subcategory_name)
                )
            )
        );

        array.forEach((student, studentIndex) => {
            (student.subcategories || []).forEach((subcategory: any) => {
                const subcategoryIndex = allSubcategories.indexOf(subcategory.subcategory_name);
                if (subcategoryIndex !== -1) {
                    graph.push([studentIndex, subcategoryIndex, subcategory.subcategory_score]);
                }
            });
        });

        return graph;
    }

    array.forEach((item, index) => {
        if (type === "decile") {
            for (let i = 0; i < 10; i++) {
                graph.push([i, index, item[`d${i + 1}`]]);
            }
        } else if (type === "risc") {
            ["a%", "b%", "c%", "d%", "e%"].forEach((key, i) => graph.push([i, index, item[key]]));
        }
    });

    return graph;
};

export const stripHtmlTags = (html: string): string => {
    return html.replace(/<[^>]*>/g, "");
};

export const isOneOf = (value: string, values: string[]) => values.includes(value);

export const sortUserName = (
    students:  {
        id: number;
        first_name: string;
        last_name: string;
        email: string;
        email_verified_at: string | null;
    }[],
    order: "asc" | "desc") => {
    return [...students].sort((a, b) => {
        const result: any = a.last_name.localeCompare(b.last_name, "ro", {
            sensitivity: "base",
        });

        return order === "asc" ? result : -result;
    });
};

export const formatDurationRo = (timeString: string): string => {
    if (!timeString) return "";

    const [hours, minutes] = timeString.split(":").map(Number);
    const parts: string[] = [];

    if (hours > 0) {
        parts.push(`${hours}h`);
    }

    if (minutes > 0 || hours === 0) {
        const unit = minutes === 1 ? "minut" : "minute";
        parts.push(`${minutes} ${unit}`);
    }

    return parts.join(" ");
};

